import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Paper,
    AppBar,
    Toolbar,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Typography, Modal
} from '@mui/material';
import {
    addCustomFieldDefinition,
    addProduct, deleteCustomFieldDefinition, deleteProduct, fetchCustomFieldDef,
    fetchProducts,
    fetchUserData
} from '../services/api';
import {UserProfile} from '../services/types';
import Icon from "../components/Icon";
import {Add} from "@mui/icons-material";

const FIELD_TYPES = [
    // { entity: 'SLIDE', title: 'Поля для слайдов' },
    { entity: 'PRESENTATION', title: 'Поля для презентаций' },
    { entity: 'VISIT', title: 'Поля для сценариев' }
];

type Field = {
    id: number;
    entity: 'PRESENTATION' | 'SCENARIO';
    title: string;
    description: string;
    required: boolean;
};

let fieldId = 1;

const Profile: React.FC = () => {
    const [profile, setProfile] = useState<UserProfile>({
        id: 0,
        role: '',
        name: '',
        login: '',
        status: '',
        createdAt: ''
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [fields, setFields] = useState<Record<string, Field[]>>({
        SLIDE: [],
        PRESENTATION: [],
        VISIT: []
    });

    // Для модального окна добавления
    const [openAdd, setOpenAdd] = useState<null | string>(null);
    const [newTitle, setNewTitle] = useState('');
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

    // Для модального окна редактирования
    const [editField, setEditField] = useState<Field | null>(null);

    useEffect(() => {
        fetchUserProfile();
        fetchCustomFieldDefinition();
    }, []);

    const fetchUserProfile = async () => {
        try {
            const data = await fetchUserData();
            setProfile(data);
        } catch (error) {
            console.error('Error fetching user profile:', error);
            setError('Failed to load user profile');
        }
    };

    const fetchCustomFieldDefinition = async () => {
        try {
            setLoading(true);
            const data = await fetchCustomFieldDef(1);
            const grouped: Record<string, Field[]> = { SLIDE: [], PRESENTATION: [], VISIT: [] };
            for (const item of data.items) {
                if (grouped[item.entity]) {
                    grouped[item.entity].push({
                        id: item.id,
                        entity: item.entity,
                        title: item.title,
                        description: item.description,
                        required: item.required
                    });
                }
            }
            setFields(grouped);
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
    }, []);

    const handleProfileUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
    };

    // Добавление поля
    const handleAddField = async(entity: string) => {
        if (!newTitle.trim()) return;
        const addData = {
            entity: entity as Field['entity'],
            title: newTitle,
            description: '',
            required: false
        }
        await addCustomFieldDefinition(addData);
        fetchCustomFieldDefinition()
        setOpenAdd(null)
    };
    const handleEditSave = async() => {
        if (!editField) return;
        setFields(prev => ({
            ...prev,
            [editField.entity]: prev[editField.entity].map(f => f.id === editField.id ? editField : f)
        }));
        setEditField(null);
    };

    const handleDeleteRequest = () => {
        setIsDeleteConfirmOpen(true);
    };
    const handleDeleteConfirm = async () => {
        try {
            await deleteCustomFieldDefinition(editField?.id);
            setEditField(null)
            setIsDeleteConfirmOpen(false);
            fetchCustomFieldDefinition();
        } catch (error) {
            console.error('Error deleting product:', error);
            setError('Failed to delete product');
        }
    };

    return (
        <Box>
            <AppBar position="static">
                <Toolbar className='toolbar'>
                    Профиль пользователя
                </Toolbar>
            </AppBar>
            <Box className='profilePage' sx={{ p: 3 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Paper className="profileContainer" sx={{ p: 3 }}>
                            <form onSubmit={handleProfileUpdate}>
                                <TextField
                                    fullWidth
                                    margin="normal"
                                    label="Имя"
                                    value={profile.name}
                                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                                />
                                <TextField
                                    fullWidth
                                    margin="normal"
                                    label="Email"
                                    value={profile.login}
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                />
                            </form>
                        </Paper>
                    </Grid>

                </Grid>
                <Grid container spacing={2} sx={{ mt: 2 }}>
                    {FIELD_TYPES.map((type) => (
                        <Grid item xs={12} md={6} key={type.entity}>
                            <Paper className="profileContainer" sx={{ p: 2 }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="h6">{type.title}</Typography>
                                    <Button onClick={() => setOpenAdd(type.entity)}>
                                        <Add/>
                                    </Button>
                                </Box>
                                <List>
                                    {fields[type.entity].map(field => (
                                        <ListItem key={field.id} disablePadding>
                                            <ListItemButton onClick={() => setEditField(field)}>
                                                <ListItemText primary={field.title} />
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </List>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
                {/* Модальное окно добавления */}
                <Modal open={!!openAdd} onClose={() => setOpenAdd(null)}>
                    <Box sx={{flexGrow:1, minHeight: '100vh', display: 'flex', justifyContent:'center', alignItems:'center'}}>
                        <Box className="modalFiled" sx={{ position: 'relative', bgcolor: 'background.paper', boxShadow: 24, maxWidth: 600 }}>
                            <Typography variant="h6" sx={{mb: 2}}>
                                Добавить поле
                            </Typography>
                            <TextField
                                autoFocus
                                label="Название"
                                fullWidth
                                value={newTitle}
                                onChange={e => setNewTitle(e.target.value)}
                            />
                            <Box sx={{display:'flex', justifyContent: 'space-between', mt: 2, gap: 2}}>
                                <Button className="blueModalButton" variant="contained" onClick={() => handleAddField(openAdd!)}>
                                    Сохранить
                                </Button>
                                <Button className="blueModalButton" variant="outlined" onClick={() => setOpenAdd(null)} style={{color: 'red', borderColor: 'red'}}>
                                    Отмена
                                </Button>

                            </Box>
                        </Box>
                    </Box>
                </Modal>
                {/* Модальное окно редактирования */}
                <Modal open={!!editField} onClose={() => setOpenAdd(null)}>
                    <Box sx={{flexGrow:1, minHeight: '100vh', display: 'flex', justifyContent:'center', alignItems:'center'}}>
                        <Box className="modalFiled" sx={{ position: 'relative', bgcolor: 'background.paper', boxShadow: 24, maxWidth: 600 }}>
                            <Typography variant="h6" sx={{mb: 3}}>
                                Редактировать поле
                            </Typography>
                            <TextField
                                label="Название"
                                fullWidth
                                value={editField?.title || ''}
                                onChange={e => setEditField(editField ? { ...editField, title: e.target.value } : null)}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label="Описание"
                                fullWidth
                                multiline
                                minRows={2}
                                value={editField?.description || ''}
                                onChange={e => setEditField(editField ? { ...editField, description: e.target.value } : null)}
                            />
                            <Box sx={{display:'flex', justifyContent: 'space-between', mt: 2, gap: 2}}>
                                <Button className="blueModalButton" variant="contained" onClick={() => handleEditSave()}>
                                    Сохранить
                                </Button>
                                <Button className="blueModalButton" variant="outlined" onClick={() => setEditField(null)} style={{color: 'red', borderColor: 'red'}}>
                                    Отмена
                                </Button>
                                <Button
                                    className="addToobarBtn"
                                    variant="contained"
                                    onClick={handleDeleteRequest}
                                >
                                    <Icon name="remove" />
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Modal>

                <Modal open={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)}>
                    <Box sx={{flexGrow:1, minHeight: '100vh', display: 'flex', justifyContent:'center', alignItems:'center'}}>
                        <Box className="modalFiled" sx={{ position: 'relative', bgcolor: 'background.paper', boxShadow: 24, maxWidth: 400, p: 3 }}>
                            <Typography variant="h6" sx={{mb: 2}}>
                                Вы действительно хотите удалить поле?
                            </Typography>
                            <Box sx={{display:'flex', justifyContent: 'flex-end', gap: 2}}>
                                <Button variant="contained" color="error" onClick={handleDeleteConfirm}>Удалить</Button>
                                <Button variant="outlined" onClick={() => setIsDeleteConfirmOpen(false)}>Отмена</Button>
                            </Box>
                        </Box>
                    </Box>
                </Modal>
            </Box>
        </Box>
    );
};

export default Profile;
